import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  CircularProgress,
  IconButton,
  TextField,
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { FaEdit, FaEye } from 'react-icons/fa';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";

// Components
import CustomButton from "../../components/common/CustomButton/CustomButton";
import { CustomTabPanel, SubHeaderTitle } from "../../components/common/index";
import { CommonData, CommonFunctions } from "../PageImports";
import ErrorPage from "../Error/ErrorPage";
import Loader from "../../utils/Loader";

// Utilities
import { axiosFunction } from '../../components/commonFunctions/axiosFunction';
import { v2ReactURL } from '../../utils/constant';
import { capitalizeFirstChar, debounce } from '../Contact/MainDataTable';

const {
  Wrapper,
  WrapperSubHeader,
  WrapperContent,
  WrapperCard,
  Row,
  CustomDataTable,
} = CommonData;

const { toastMessage } = CommonFunctions;

// Remove the interface and just use props
const ApartmentManagementMain = ({ navUrl }) => {
  const tabView = navUrl;

  // Permission state
  const [userPermissions, setUserPermissions] = useState({
    canView: false,
    canInsert: false,
    canEdit: false,
    canDelete: false,
    loading: true,
    error: null
  });

  const [selectedApartment, setSelectedApartment] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [apartments, setApartments] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    pages: 1,
    perpage: 10,
    total: 0,
  });
  const [pageOption, setPageOption] = useState('10');
  const [searchValue, setSearchValue] = useState('');
  const [error, setError] = useState(null);
  const [checkedValue, setCheckedValue] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    apartmentName: '',
    id: 0
  });

  const [showValidation, setShowValidation] = useState(false);

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
    vertical: 'bottom',
    horizontal: 'right'
  });

  // Show snackbar function
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
      vertical: 'bottom',
      horizontal: 'right'
    });
  };

  // Fetch user permissions
  const fetchUserPermissions = async () => {
    try {
      setUserPermissions(prev => ({ ...prev, loading: true, error: null }));

      const response = await axiosFunction(
        `${v2ReactURL}/native/getuserrolepermission`,
        'GET'
      );

      if (response.data?.status && response.data.response?.reportListResponse) {
        const reportList = response.data.response.reportListResponse;

        if (Array.isArray(reportList)) {
          const apartmentPermissions = reportList.find((item) =>
            item.type === 'apartment' || item.type === 'building'
          );

          if (apartmentPermissions && apartmentPermissions.permissionids) {
            let permissionArray = [];

            if (typeof apartmentPermissions.permissionids === 'string') {
              permissionArray = apartmentPermissions.permissionids.split(',').map((id) => id.trim());
            } else if (Array.isArray(apartmentPermissions.permissionids)) {
              permissionArray = apartmentPermissions.permissionids.map((id) => id.toString());
            }

            const canView = permissionArray.includes('2');

            setUserPermissions({
              loading: false,
              canView: canView,
              canInsert: permissionArray.includes('1'),
              canEdit: permissionArray.includes('3'),
              canDelete: permissionArray.includes('4'),
              error: null
            });

            return canView;
          } else {
            setUserPermissions({
              loading: false,
              canView: false,
              canInsert: false,
              canEdit: false,
              canDelete: false,
              error: 'No apartment permissions found'
            });
            return false;
          }
        } else {
          setUserPermissions({
            loading: false,
            canView: false,
            canInsert: false,
            canEdit: false,
            canDelete: false,
            error: 'Invalid response format'
          });
          return false;
        }
      } else {
        setUserPermissions({
          loading: false,
          canView: false,
          canInsert: false,
          canEdit: false,
          canDelete: false,
          error: response.data?.message || 'Failed to load permissions'
        });
        return false;
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setUserPermissions({
        loading: false,
        canView: false,
        canInsert: false,
        canEdit: false,
        canDelete: false,
        error: 'Error fetching permissions from server'
      });
      return false;
    }
  };

  // Fetch apartments from API
  const fetchApartments = async () => {
    setLoadingData(true);
    try {
      const response = await axiosFunction(
        `${v2ReactURL}/native/delivery/apartmentList`,
        'POST',
        {
          searchValue: searchValue,
          length: parseInt(pageOption),
          start: pagination.page * parseInt(pageOption),
          draw: 1
        },
        false
      );

      const data = response?.data?.response || response?.data || response;
      
      if (data?.data && Array.isArray(data.data)) {
        const apartmentList = data.data.map((apartment, index) => ({
          id: apartment.apartmentId,
          apartmentId: apartment.apartmentId,
          apartmentName: apartment.apartmentName,
          createdOn: apartment.createdOn,
          createdBy: apartment.createdBy || 'N/A',
          status: apartment.status || 1,
          index: index + 1,
          checked: false
        }));
        
        setApartments(apartmentList);
        
        const meta = data.dataTableMetaDTO || {
          page: pagination.page,
          pages: Math.ceil((data.recordsTotal || data.data.length) / parseInt(pageOption)),
          perpage: parseInt(pageOption),
          total: data.recordsTotal || data.data.length
        };
        
        setPagination({
          page: meta.page || 0,
          pages: meta.pages || 1,
          perpage: meta.perpage || parseInt(pageOption),
          total: meta.total || data.data.length
        });
      } else {
        setApartments([]);
        setPagination(prev => ({
          ...prev,
          total: 0,
          pages: 1
        }));
      }
      
      setIsLoading(false);
      setLoadingData(false);
      
    } catch (error) {
      console.error("Error fetching apartments:", error);
      showSnackbar('Error loading apartment details. Please try again.', 'error');
      setLoadingData(false);
      setIsLoading(false);
      setApartments([]);
    }
  };

  // Save apartment (create or update)
  const saveApartment = async () => {
    if (!formData.apartmentName.trim()) {
      setShowValidation(true);
      return;
    }

    setSaving(true);
    try {
      const apartmentData = {
        id: formData.id || 0,
        apartmentName: formData.apartmentName.trim()
      };

      const response = await axiosFunction(
        `${v2ReactURL}/native/delivery/saveApartment`,
        'POST',
        apartmentData
      );

      if (response.status === 200 || response.status === 201 || response.data?.status) {
        showSnackbar(formData.id ? 'Apartment updated successfully!' : 'Apartment created successfully!', 'success');
        
        setOpenEditModal(false);
        resetForm();
        fetchApartments();
      } else {
        showSnackbar(response.data?.message || `Failed to ${formData.id ? 'update' : 'create'} apartment`, 'error');
      }
    } catch (error) {
      console.error("Error saving apartment:", error);
      showSnackbar(`Error ${formData.id ? 'updating' : 'creating'} apartment details`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete apartment
  const handleDeleteApartment = async (apartmentId) => {
    // if (!window.confirm("Are you sure you want to delete this apartment?")) {
    //   return;
    // }

    setDeleting(true);
    try {
      const response = await axiosFunction(
        `${v2ReactURL}/native/delivery/apartment/${apartmentId}/delete`,
        'POST'
      );

      if (response.status === 200 || response.status === 204 || response.data?.status) {
        showSnackbar('Apartment deleted successfully!', 'success');
        
        fetchApartments();
      } else {
        showSnackbar(response.data?.message || 'Failed to delete apartment', 'error');
      }
    } catch (error) {
      console.error("Error deleting apartment:", error);
      showSnackbar('Error deleting apartment', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Handle edit
  const handleEditApartment = (apartment) => {
    setSelectedApartment(apartment);
    setFormData({
      apartmentName: apartment.apartmentName,
      id: apartment.apartmentId || apartment.id
    });
    setShowValidation(false);
    setOpenEditModal(true);
  };

  // Handle view
  const handleViewApartment = (apartment) => {
    setSelectedApartment(apartment);
    setOpenViewModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      apartmentName: '',
      id: 0
    });
    setSelectedApartment(null);
    setShowValidation(false);
  };

  // Handle form change
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Initialize data
  const initializeData = async () => {
    const hasViewPermission = await fetchUserPermissions();

    if (hasViewPermission) {
      await fetchApartments();
    } else {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializeData();
  }, []);

  // Re-fetch when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 0 }));
      fetchApartments();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Re-fetch when page changes
  useEffect(() => {
    if (!isLoading) {
      fetchApartments();
    }
  }, [pagination.page, pageOption]);

  const handleChangePageOption = (e) => {
    const selectedValue = e.target.value;
    setPageOption(selectedValue);
    setPagination(prev => ({ 
      ...prev, 
      page: 0, 
      perpage: parseInt(selectedValue) 
    }));
  };

  const handlePageChange = (_, page) => {
    setPagination(prev => ({ ...prev, page: page - 1 }));
  };

  const handleSearchData = useCallback(
    debounce((searchValue) => {
      setPagination(prev => ({ ...prev, page: 0 }));
      fetchApartments();
    }, 500),
    [pageOption]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    handleSearchData(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const dateParts = dateString.split(/[-/:\s]/);
      if (dateParts.length >= 3) {
        if (dateParts[0].length === 2 && dateParts[1].length === 2) {
          const [day, month, year] = dateParts;
          const date = new Date(`${year}-${month}-${day}`);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        }
      }
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setSelectedApartment(null);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    resetForm();
  };

  // Table columns
  const columnsDataTableApartments = [
    {
      field: "srNo",
      headerName: "Sr. No",
      width: 80,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '14px' }}>
          {pagination.page * pagination.perpage + params.row.index}
        </Typography>
      ),
    },
    {
      field: "apartmentName",
      headerName: "Apartment Name",
      width: 200,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          fontWeight="500"  
          // onClick={() => handleViewApartment(params.row)} 
          style={{ 
            // cursor: 'pointer', 
            color: '#1f1f1fff', 
            // textDecoration: 'underline'
          }}
        >
          {params.row.apartmentName}
        </Typography>
      ),
    },
    
    {
      field: "createdOn",
      headerName: "Created On",
      width: 150,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '14px' }}>
          {formatDate(params.row.createdOn || '')}
        </Typography>
      ),
    },
   
    {
      field: "Action",
      headerName: "Actions",
      width: 150,
      defaultChecked: true,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          {userPermissions.canEdit && (
            <IconButton
              size="small"
              onClick={() => handleEditApartment(params.row)}
              sx={{
                color: 'warning.main',
                '&:hover': { backgroundColor: 'warning.light', color: 'white' }
              }}
            >
              <FaEdit />
            </IconButton>
          )}
          {userPermissions.canDelete && (
            <IconButton
              size="small"
              onClick={() => handleDeleteApartment(params.row.apartmentId || params.row.id)}
              disabled={deleting}
              sx={{
                color: 'error.main',
                '&:hover': { backgroundColor: 'error.light', color: 'white' }
              }}
            >
              {deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  // Show permission loading
  if (userPermissions.loading) {
    return <Loader />;
  }

  // Check view permission
  if (!userPermissions.canView) {
    return <ErrorPage error={{ code: '403', message: 'You do not have permission to view apartments.' }} />;
  }

  if (tabView === '') {
    return (
      <ErrorPage
        error={{
          code: '403',
          message: "Whoops, access denied! Seems like you hit a dead end. Mysteries of the web, right?",
        }}
      />
    );
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <title>{navUrl}</title>
      </Helmet>
      
      <Wrapper navUrl={navUrl} customStyle={{ minHeight: "750px" }}>
        <WrapperSubHeader className="mainBreadcrumb">
          <div className="mr-auto">
            <SubHeaderTitle subTitle={capitalizeFirstChar(tabView)} />
          </div>
        </WrapperSubHeader>
        
        <WrapperContent>
          <Row>
            <WrapperCard isCollapsableAnalytics={false}>
              <CustomTabPanel>
                {/* Analytics section can be added here if needed */}
              </CustomTabPanel>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} sx={{ px: 2, pt: '10px' }}>
                <Typography variant="h6" fontWeight="bold">
                  Apartment List
                </Typography>
                
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    size="small"
                    placeholder="Search apartments..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                    }}
                    sx={{ width: 250 }}
                  />

                  {/* Create button - only show if user has insert permission */}
                  {userPermissions.canInsert && (
                    <CustomButton
                      onClick={() => {
                        resetForm();
                        setOpenEditModal(true);
                      }}
                      btnName={"Add New Apartment"}
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<AddIcon />}
                    />
                  )}
                </Box>
              </Box>

              {/* Data Table */}
              {loadingData ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress />
                </Box>
              ) : apartments.length === 0 ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <Typography variant="h6" color="textSecondary">
                    {searchValue ? 'No apartments found matching your search.' : 'No apartments available.'}
                  </Typography>
                </Box>
              ) : (
                <CustomDataTable
                  rows={apartments}
                  columns={columnsDataTableApartments}
                  paginationInfo={pagination}
                  handlePageChange={handlePageChange}
                  isCheckboxShow={false}
                  FixedTable={true}
                  pageOption={pageOption}
                  handleChangePageOption={handleChangePageOption}
                  loading={loadingData}
                  isBranchFieldExist={false}
                  setCheckedValue={setCheckedValue}
                  checkedValue={checkedValue}
                  handleApplyFilterBtn={() => {}}
                  toggleFilter={() => {}}
                  openFilter={openFilter}
                  setOpenFilter={setOpenFilter}
                  handleInputFocus={() => {}}
                  branchLocationList={[]}
                  filterData={[]}
                  setFilterData={() => {}}
                  handleSearch={() => {}}
                  contactFilteredIds={[]}
                  setContactFilteredIds={() => {}}
                  filteringData={{}}
                  setSearchInputValue={setSearchInputValue}
                  searchInputValue={searchInputValue}
                  linkField={"apartmentName"}
                  linkFieldNav={() => '#'}
                />
              )}
            </WrapperCard>
          </Row>
        </WrapperContent>
      </Wrapper>

      {/* View Apartment Modal */}
      <Dialog 
        open={openViewModal} 
        onClose={handleCloseViewModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{selectedApartment?.apartmentName} Details</Typography>
            <IconButton onClick={handleCloseViewModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedApartment && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px" }}>
                  APARTMENT NAME
                </Typography>
                <Typography variant="body2" sx={{ color: "#292929", fontSize: "13px" }}>
                  {selectedApartment.apartmentName}
                </Typography>
              </Box>
 
 

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px" }}>
                  CREATED ON
                </Typography>
                <Typography variant="body2" sx={{ color: "#292929", fontSize: "13px" }}>
                  {formatDate(selectedApartment.createdOn || '')}
                </Typography>
              </Box>

            
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewModal} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Apartment Modal */}
      <Dialog 
        open={openEditModal} 
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {formData.id ? 'Edit Apartment' : 'Add New Apartment'}
            </Typography>
            <IconButton onClick={handleCloseEditModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Apartment Name"
              value={formData.apartmentName}
              onChange={(e) => handleFormChange('apartmentName', e.target.value)}
              disabled={false}
              margin="normal"
              variant="outlined"
              size="medium"
              required
              error={showValidation && !formData.apartmentName.trim()}
              helperText={showValidation && !formData.apartmentName.trim() ? "Apartment name is required" : ""}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseEditModal} 
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={saveApartment} 
            color="primary" 
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : (formData.id ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications - BOTTOM RIGHT */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ 
          vertical: snackbar.vertical || 'bottom', 
          horizontal: snackbar.horizontal || 'right' 
        }}
        sx={{
          '& .MuiAlert-root': {
            fontSize: '14px',
            fontWeight: 500
          }
        }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ApartmentManagementMain;